import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { Link, withRouter } from 'react-router-dom';
import TextInput from '../form-fields/text-input';
import GenericForm from '../form-fields/generic-form';
import { register, CHANGE_AUTH } from '../../redux/modules/authentication';
import { errorPropTypes } from '../../util/proptype-utils';
import styles from './login.module.css';
import { getComponentTranslator } from '../../util/i18n';
import { Translate } from 'react-i18nify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';  // Importera FontAwesomeIcon-komponenten
import { faChalkboardTeacher, faUser } from '@fortawesome/free-solid-svg-icons';

// Översättningsfunktion
const translate = getComponentTranslator('register');

// Valideringsfunktion
const validate = values => {
  const errors = {};

  // Kontrollera att förnamn och efternamn är ifyllda
  if (!values.name || !values.name.first) {
    errors.name = { ...errors.name, first: 'You must enter a full name.' };
  }
  if (!values.name || !values.name.last) {
    errors.name = { ...errors.name, last: 'You must enter a full name.' };
  }

  // Kontrollera att e-post är ifylld och giltig
  if (!values.email) {
    errors.email = 'You must enter a valid email address.';
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
    errors.email = 'Invalid email address.';
  }

  // Kontrollera att lösenord är ifyllt
  if (!values.password) {
    errors.password = 'You must enter a password.';
  }

  // Kontrollera att lösenord och lösenordsbekräftelse matchar
  if (values.password !== values.passwordConfirm) {
    errors.passwordConfirm = 'Passwords must match.';
  }

  return errors;
};

// Redux form setup
const form = reduxForm({
  form: 'register',
  validate, // Lägg till valideringsfunktionen här
});

class Register extends Component {
  state = {
    step: 1, // För att hålla koll på vilket steg vi är på
    role: '', // För att hålla reda på vald roll (lärare eller deltagare)
    teacherCode: '', // För att hålla reda på inmatad lärarkod
  };

  static propTypes = {
    handleSubmit: PropTypes.func,
    register: PropTypes.func,
    errors: errorPropTypes,
    message: PropTypes.string,
    loading: PropTypes.bool,
    history: PropTypes.object, // För att använda history för omdirigering
  };
  

  static formSpec = [
    { id: 'firstName', name: 'name.first', label: translate('firstName'), type: 'text', placeholder: translate('firstNameExample'), component: TextInput },
    { id: 'lastName', name: 'name.last', label: translate('lastName'), type: 'text', placeholder: translate('lastNameExample'), component: TextInput },
    { id: 'email', name: 'email', label: translate('email'), type: 'email', placeholder: translate('emailExample'), component: TextInput },
    { id: 'password', name: 'password', label: translate('password'), type: 'password', placeholder: '********', component: TextInput },
    { id: 'passwordConfirm', name: 'passwordConfirm', label: translate('confirmPassword'), type: 'password', placeholder: '********', component: TextInput },
  ];

  // Hantera när användaren trycker på "Fortsätt" för att gå till steg 2
  handleContinue = () => {
    this.setState({ step: 2 });
  };

  // Hantera rollval (lärare eller deltagare)
  handleRoleSelect = role => {
    this.setState({ role });
  };

  // Hantera lärarkod
  handleTeacherCodeChange = event => {
    this.setState({ teacherCode: event.target.value });
  };

  // Hantera när användaren skickar hela registreringen
  handleFormSubmit = formProps => {
    const { register } = this.props;
    const { role, teacherCode } = this.state;

    // Kontrollera att användaren har valt en roll
    if (!role) {
      alert(translate('register.selectRole'));
      return;
    }

    // Om användaren är lärare, kontrollera lärarkoden
    if (role === 'teacher' && teacherCode !== 'Healthweek@nti') {
      alert(translate('register.invalidTeacherCode'));
      return;
    }

    // Mappa rollvalet till korrekt rollvärde i databasen
    let userRole = 'participant';
    if (role === 'teacher') {
      userRole = 'teacher';
    }

    // Lägg till roll och lärarkod i registreringsuppgifterna
    const updatedFormProps = {
      ...formProps,
      role: userRole,
      teacherCode: role === 'teacher' ? teacherCode : null,
    };

    // Skicka registreringsformuläret
    register(updatedFormProps);

    // Omdirigera användaren till startsidan efter registreringen
    this.props.history.push('/dashboard');
  };

  render() {
    const { handleSubmit, errors, message, loading } = this.props;
    const { step } = this.state;

    return (
      <div className={styles.loginContainer}>
        <div className={styles.leftSection}>
          <video 
            autoPlay 
            muted 
            loop 
            className={styles.backgroundVideo}
            playsInline
          >
            <source src="https://videos.pexels.com/video-files/3125907/3125907-sd_640_360_25fps.mp4" type="video/mp4" />
            Din webbläsare stöder inte video-taggen.
          </video>
          <h2>Everything you need,<br />to make anything you want.</h2>
          <p>Dozens of creative tools to ideate, generate<br />and edit content like never before.</p>
        </div>

        <div className={styles.authBox}>
          <h1 className={styles.h1}>Create your account</h1>
          
          {/* Steg 1: Personliga uppgifter */}
          {step === 1 && (
            <>
              <p className={styles.signUpText}>
                Already have an account? <Link to="/login">Log in</Link>
              </p>
              <GenericForm
                onSubmit={handleSubmit(this.handleContinue)}
                errors={errors}
                message={message}
                formSpec={Register.formSpec}
                submitText={translate('continue')}
              />
            </>
          )}

          {/* Steg 2: Välj roll */}
          {step === 2 && (
            <div className={styles.roleSelectionContainer}>
              <h2>{translate('register.selectRole')}</h2>
              <button 
                className={`${styles.roleButton} ${this.state.role === 'teacher' ? styles.selected : ''}`} 
                onClick={() => this.handleRoleSelect('teacher')}
              >
                <FontAwesomeIcon 
                  icon={faChalkboardTeacher} 
                  style={{ fontSize: '24px', marginRight: '8px' }} 
                />
                {translate('register.teacher')}
              </button>

              <button 
                className={`${styles.roleButton} ${this.state.role === 'participant' ? styles.selected : ''}`} 
                onClick={() => this.handleRoleSelect('participant')}
              >
                <FontAwesomeIcon 
                  icon={faUser} 
                  style={{ fontSize: '24px', marginRight: '8px' }} 
                />
                {translate('register.participant')}
              </button>

              {this.state.role === 'teacher' && (
                <div className={styles.teacherCodeInput}>
                  <label>{translate('register.teacherCode')}</label>
                  <input
                    type="text"
                    value={this.state.teacherCode}
                    onChange={this.handleTeacherCodeChange}
                    placeholder={translate('register.enterTeacherCode')}
                  />
                </div>
              )}

              {this.state.role && (
                <button 
                  className={styles.confirmButton} 
                  onClick={handleSubmit(this.handleFormSubmit)}
                >
                  {translate('register.confirm')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ authentication }) => ({
  errors: authentication.errors[CHANGE_AUTH],
  message: authentication.messages[CHANGE_AUTH],
  loading: authentication.loading[CHANGE_AUTH],
  authenticated: authentication.authenticated,
});

export default withRouter(connect(mapStateToProps, { register })(form(Register)));
